<template>
  <v-container>
    <h1 class="text-h5 mb-4">ARCA — Homologación</h1>
    <v-alert type="warning" prominent>ENTORNO DE PRUEBA. No emite comprobantes fiscales de producción.</v-alert>
    <v-alert v-if="status" :type="status.configured ? 'info' : 'warning'">{{ status.message }}</v-alert>
    <v-alert v-if="error" type="error" role="alert">{{ error }}</v-alert>
    <v-btn class="mb-4" :loading="busy" @click="initialize">Revisar configuración</v-btn>

    <v-card class="mb-6">
      <v-card-title>Consulta fiscal por CUIT</v-card-title>
      <v-card-text>
        <v-text-field v-model="cuit" label="CUIT de prueba" placeholder="20-12345678-6" :disabled="busy" />
        <v-btn color="primary" :disabled="!configured || busy" @click="lookup">Consultar constancia</v-btn>
        <div v-if="taxpayer" class="mt-4">
          <p><strong>{{ taxpayer.name || 'Sin nombre informado' }}</strong> — {{ taxpayer.cuit }}</p>
          <p>Estado: {{ taxpayer.status || 'No informado' }} · Tipo: {{ taxpayer.personType || 'No informado' }}</p>
          <p>Domicilio: {{ taxpayer.address || 'No informado' }} · {{ taxpayer.province || '' }}</p>
          <p>Monotributo: {{ taxpayer.monotributo ? 'Sí' : 'No informado' }}</p>
          <p>Impuestos: {{ taxpayer.taxes.map(item => item.name || item.id).join(', ') || 'No informados' }}</p>
          <p>Actividades: {{ taxpayer.activities.map(item => item.name || item.id).join(', ') || 'No informadas' }}</p>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>Factura electrónica de prueba</v-card-title>
      <v-card-text>
        <v-alert v-if="billingMessage" type="info">{{ billingMessage }}</v-alert>
        <v-form @submit.prevent="confirm = true">
          <v-row>
            <v-col cols="12" md="4"><v-select v-model="form.CbteTipo" :items="types" item-text="name" item-value="id" label="Tipo de factura" :disabled="busy || locked" @change="loadCatalogs" /></v-col>
            <v-col cols="12" md="4"><v-select v-model="form.PtoVta" :items="points" item-text="name" item-value="id" label="Punto de venta CAE (ARCA)" :disabled="busy || locked" /></v-col>
            <v-col cols="12" md="4"><v-select v-model="form.Concepto" :items="concepts" item-text="name" item-value="id" label="Concepto" :disabled="busy || locked" /></v-col>
            <v-col cols="12" md="4"><v-select v-model="form.DocTipo" :items="documents" item-text="name" item-value="id" label="Documento receptor" :disabled="busy || locked" /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.DocNro" label="Número documento (0 para consumidor final)" :disabled="busy || locked" /></v-col>
            <v-col cols="12" md="4"><v-select v-model="form.CondicionIVAReceptorId" :items="conditions" item-text="name" item-value="id" label="Condición IVA receptor (ARCA)" :disabled="busy || locked" /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.CbteFch" type="date" label="Fecha de factura" :disabled="busy || locked" /></v-col>
            <template v-if="form.Concepto !== 1">
              <v-col cols="12" md="4"><v-text-field v-model="form.FchServDesde" type="date" label="Servicios desde" :disabled="busy || locked" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="form.FchServHasta" type="date" label="Servicios hasta" :disabled="busy || locked" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="form.FchVtoPago" type="date" label="Vencimiento de pago" :disabled="busy || locked" /></v-col>
            </template>
            <v-col cols="12" md="4"><v-text-field v-model.number="form.ImpNeto" type="number" min="0" step="0.01" :label="form.CbteTipo === 11 ? 'Importe factura C (PES)' : 'Neto gravado (PES)'" :disabled="busy || locked" /></v-col>
            <template v-if="form.CbteTipo !== 11">
              <v-col cols="12" md="4"><v-select v-model="vatId" :items="vatOptions" item-text="name" item-value="id" label="Alícuota IVA" :disabled="busy || locked" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model.number="form.ImpTotConc" type="number" min="0" step="0.01" label="No gravado (PES)" :disabled="busy || locked" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model.number="form.ImpOpEx" type="number" min="0" step="0.01" label="Exento (PES)" :disabled="busy || locked" /></v-col>
            </template>
          </v-row>
          <p>IVA: {{ invoice.ImpIVA }} · Total PES: <strong>{{ invoice.ImpTotal }}</strong></p>
          <v-btn color="warning" type="submit" :disabled="!canIssue || locked || busy">Emitir factura de prueba</v-btn>
        </v-form>
        <v-divider class="my-4" />
        <v-alert v-if="locked" type="warning">Solicitud conservada. No reemita ni cambie la clave ante un resultado incierto. La conciliación no emite un comprobante nuevo.</v-alert>
        <v-text-field v-model="key" label="Clave de solicitud (conserve para recuperación)" :readonly="locked" :disabled="busy" />
        <v-btn :disabled="!configured || !key || busy" @click="reconcile">Consultar / conciliar solicitud</v-btn>
        <v-btn class="ml-2" :disabled="busy || !terminal" @click="newRequest">Nueva solicitud</v-btn>
        <v-alert v-if="result" class="mt-4" :type="result.state === 'accepted' ? 'success' : 'warning'">
          Estado: {{ stateLabels[result.state] || result.state }} · Número: {{ result.number || 'No asignado' }}
          <span v-if="result.cae"> · CAE: {{ result.cae }} · Vencimiento: {{ result.expires }}</span>
          <p v-if="result.message">{{ result.message }}</p>
        </v-alert>
      </v-card-text>
    </v-card>
    <v-dialog v-model="confirm" max-width="500">
      <v-card>
        <v-card-title>Confirmar factura de prueba</v-card-title>
        <v-card-text>Se enviará una factura a ARCA HOMOLOGACIÓN por PES {{ invoice.ImpTotal }}. No tiene validez fiscal de producción. ¿Confirma los datos del receptor y los importes?</v-card-text>
        <v-card-actions><v-btn text @click="confirm = false">Cancelar</v-btn><v-btn color="warning" :disabled="busy || !canIssue || locked" @click="issue">Confirmar prueba</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
const rates = { 3: 0, 4: 10.5, 5: 21, 6: 27, 7: 5, 8: 2.5 }
const round = amount => Math.round(amount * 100) / 100
export default {
  name: 'ArcaHomologation',
  data() {
    return {
      status: null, busy: false, error: '', billingMessage: '', cuit: '', taxpayer: null,
      confirm: false, locked: false, key: '', result: null, points: [], conditions: [], vatId: 5,
      types: [{ id: 1, name: 'Factura A' }, { id: 6, name: 'Factura B' }, { id: 11, name: 'Factura C' }],
      concepts: [{ id: 1, name: 'Productos' }, { id: 2, name: 'Servicios' }, { id: 3, name: 'Productos y servicios' }],
      documents: [{ id: 80, name: 'CUIT' }, { id: 96, name: 'DNI' }, { id: 99, name: 'Consumidor final' }],
      vatOptions: Object.entries(rates).map(([id, rate]) => ({ id: Number(id), name: `${rate}%` })),
      stateLabels: { accepted: 'Autorizada (prueba)', rejected: 'Rechazada', unknown: 'Incierto — no reemitir', pending: 'Pendiente — no reemitir', allocating: 'Asignación pendiente — revisión operativa' },
      form: { PtoVta: null, CbteTipo: 6, Concepto: 1, DocTipo: 80, DocNro: '', CondicionIVAReceptorId: null, CbteFch: '', ImpNeto: 0, ImpTotConc: 0, ImpOpEx: 0, FchServDesde: '', FchServHasta: '', FchVtoPago: '' }
    }
  },
  computed: {
    configured() { return Boolean(this.status && this.status.configured) },
    canIssue() { return Boolean(this.status && this.status.ready && this.points.length && this.conditions.length) },
    terminal() { return Boolean(this.result && ['accepted', 'rejected'].includes(this.result.state)) },
    storageKey() { return `arca-homologation-request:${this.$store.state.username}` },
    invoice() {
      const isC = this.form.CbteTipo === 11
      const net = Number(this.form.ImpNeto)
      const iva = isC ? 0 : round(net * rates[this.vatId] / 100)
      const invoice = {
        PtoVta: this.form.PtoVta, CbteTipo: this.form.CbteTipo, Concepto: this.form.Concepto,
        DocTipo: this.form.DocTipo, DocNro: this.form.DocNro,
        CondicionIVAReceptorId: this.form.CondicionIVAReceptorId,
        CbteFch: this.form.CbteFch.replace(/-/g, ''), ImpNeto: net, ImpIVA: iva,
        ImpTotConc: isC ? 0 : Number(this.form.ImpTotConc), ImpOpEx: isC ? 0 : Number(this.form.ImpOpEx),
        Iva: isC ? [] : [{ Id: this.vatId, BaseImp: net, Importe: iva }]
      }
      invoice.ImpTotal = round(net + iva + invoice.ImpTotConc + invoice.ImpOpEx)
      if (invoice.Concepto !== 1) {
        for (const field of ['FchServDesde', 'FchServHasta', 'FchVtoPago']) invoice[field] = this.form[field].replace(/-/g, '')
      }
      return invoice
    }
  },
  mounted() {
    this.key = sessionStorage.getItem(this.storageKey) || ''
    this.locked = Boolean(this.key)
    this.initialize()
  },
  methods: {
    async initialize() {
      this.busy = true; this.error = ''; this.points = []; this.conditions = []
      try {
        this.status = await this.$store.dispatch('arca/status')
        if (this.configured) await this.loadCatalogs()
      } catch (error) { this.status = null; this.error = error.message } finally { this.busy = false }
    },
    async loadCatalogs() {
      this.points = []; this.conditions = []; this.billingMessage = ''
      try {
        const data = await this.$store.dispatch('arca/catalogs', this.form.CbteTipo)
        this.points = data.points; this.conditions = data.conditions
        if (!this.points.length) this.billingMessage = 'No hay puntos de venta CAE habilitados en homologación.'
        if (!this.locked) { this.form.PtoVta = null; this.form.CondicionIVAReceptorId = null }
      } catch (error) { this.billingMessage = `Facturación no disponible: ${error.message}` }
    },
    async lookup() {
      this.busy = true; this.error = ''; this.taxpayer = null
      try { this.taxpayer = await this.$store.dispatch('arca/lookup', this.cuit) } catch (error) { this.error = error.message } finally { this.busy = false }
    },
    async issue() {
      if (!this.canIssue || this.locked || this.busy) return
      this.confirm = false; this.busy = true; this.error = ''; this.result = null
      try {
        if (!this.key) this.key = window.crypto.randomUUID()
        // Preserve the key BEFORE submission, including network errors/reloads.
        sessionStorage.setItem(this.storageKey, this.key)
        this.locked = true
        this.result = await this.$store.dispatch('arca/issue', { invoice: this.invoice, key: this.key })
      } catch (error) {
        this.error = error.message
        if (['ARCA_INVALID_REQUEST', 'ARCA_FORBIDDEN', 'ARCA_CONFIGURATION_REQUIRED', 'ARCA_NUMBERING_BLOCKED'].includes(error.code)) {
          this.locked = false
          sessionStorage.removeItem(this.storageKey)
          // A definitive busy response occurs before claiming this attempt.
          // No durable request exists for its key; a fresh attempt is safe.
          if (error.code === 'ARCA_NUMBERING_BLOCKED') this.key = ''
        }
      } finally { this.busy = false }
    },
    async reconcile() {
      this.busy = true; this.error = ''
      try {
        this.result = await this.$store.dispatch('arca/reconcile', this.key)
        this.locked = true
        sessionStorage.setItem(this.storageKey, this.key)
      } catch (error) { this.error = error.message } finally { this.busy = false }
    },
    newRequest() {
      if (!this.terminal) return
      this.locked = false; this.key = ''; this.result = null; this.error = ''
      sessionStorage.removeItem(this.storageKey)
    }
  }
}
</script>
